/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {FC, useState} from 'react';
import Button from '@react/react-spectrum/Button';
import Close from '@react/react-spectrum/Icon/Close';
import open from '../../../utils/open';
import getConfig from '../../../config';
import {AppContextType, SophiaBannerItem} from '../../containers/AppHome/types';

type AppHomeBannerProps = {
	intl: any;
	bannerData: SophiaBannerItem | null;
	appContextType: AppContextType;
};

const AppHomeBanner: FC<AppHomeBannerProps> = (props) => {
	const {bannerData, appContextType} = props;

	const [isOpen, setIsOpen] = useState(true);

	const closeBanner = (bannerId) => {

		// Make call to TS to store the banner
		const trackingCategory = 'web-apphomes-banner';
		window.cchome.util.logger.info('AppHomeBanner', 'Set banner as closed/completed');
		window.trackingService.setCompletionStatus({
			resource: bannerId,
			category: trackingCategory,
		}, 'complete');

		setIsOpen(false);
	};

	const onClickTakeTour = () => {
		const webUrl = appContextType === 'photoshopweb' ? getConfig('PHOTOSHOP_WEB') : getConfig('ILLUSTRATOR_WEB');
		const tourUrl = `${webUrl}?learnid=5AD1CFE88C3E`;
		open(tourUrl, '_self');
	};

	let appHomeBanner;
	if (bannerData) {
		try {
			// as agreed w/ Tondo, this is the unique id for the banner
			const bannerId = bannerData.ingestDescription;

			const buttonStyle: React.CSSProperties = {
				color: bannerData.colorTextList && bannerData.colorTextList[0] && bannerData.colorTextList[0],
				borderColor: bannerData.colorTextList && bannerData.colorTextList[0] && bannerData.colorTextList[0],
			};

			// appHomeData.data.btnVariant ? appHomeData.data.btnVariant : 'primary'
			const takeTourButton
				= window.cchome.util.hasFeature('tour_button') && (appContextType === 'photoshopweb' || appContextType === 'illustratorweb') ? (
					<Button style={buttonStyle} label={bannerData.textList && bannerData.textList[2]} labelvariant={'primary'} onClick={onClickTakeTour} />
				) : null;

			const backgroundImage
				= bannerData.imageList && bannerData.imageList[0] && bannerData.imageList[0]._publishUrl;

			const topContainerStyle: React.CSSProperties = {
				backgroundColor: bannerData.backgroundColor,
				backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
				// backgroundPosition: !!extraData.backgroundPosition ? extraData.backgroundPosition : 'center',
			};

			appHomeBanner = (bannerData.isOpen && isOpen) && (
				<div className='top-container' role='banner' aria-label='Welcome tour' style={topContainerStyle}>
					<div className='top-inner left'>
						<h2
							style={{
								color:
									bannerData.colorTextList && bannerData.colorTextList[0]
										? bannerData.colorTextList[0]
										: 'inherit',
							}}
						>
							{bannerData.textList && bannerData.textList[0]}
						</h2>
						<p
							style={{
								color:
									bannerData.colorTextList && bannerData.colorTextList[0]
										? bannerData.colorTextList[0]
										: 'inherit',
							}}
						>
							{bannerData.textList && bannerData.textList[1]}
						</p>
						{takeTourButton}
					</div>
					{/* Just for this banner component, show the X so that on ff apphomes_ucs_data, we can see the X */}
					<div className='top-inner right ucs-data'>
						<Button quiet variant='action' aria-label='dismiss' icon={<Close />} onClick={() => closeBanner(bannerId)} />
					</div>
				</div>
			);
		}
		catch (e) {
			appHomeBanner = null;
		}
	}
	else {
		appHomeBanner = null;
	}

	return appHomeBanner;
};

export default AppHomeBanner;
